// Booking Status Constants
export const BOOKING_STATUS = {
  PENDING: 'pending',
  CONFIRMED: 'confirmed',
  CANCELLED: 'cancelled',
  CANCELED: 'canceled', // Alternative spelling
  COMPLETED: 'completed',
  NO_SHOW: 'no-show',
  NOSHOW: 'noshow', // Alternative spelling
} as const;

export type BookingStatus = typeof BOOKING_STATUS[keyof typeof BOOKING_STATUS];

// Payment Status Constants
export const PAYMENT_STATUS = {
  SUCCEEDED: 'succeeded',
  PENDING: 'pending',
  FAILED: 'failed',
  PROCESSING: 'processing',
  REFUNDED: 'refunded',
} as const;

export type PaymentStatus = typeof PAYMENT_STATUS[keyof typeof PAYMENT_STATUS];

// Package Status Constants
export const PACKAGE_STATUS = {
  ACTIVE: 'active',
  INACTIVE: 'inactive',
  EXPIRED: 'expired',
} as const;

export type PackageStatus = typeof PACKAGE_STATUS[keyof typeof PACKAGE_STATUS];

// Promotional Pricing Tiers
export const PROMOTIONAL_TIER = {
  REGULAR: 'regular',
  SEASONAL_SPECIAL: 'seasonal_special',
  EARLY_BIRD: 'early_bird',
  FOUNDER_OFFER: 'founder_offer',
} as const;

export type PromotionalTier = typeof PROMOTIONAL_TIER[keyof typeof PROMOTIONAL_TIER];

// Status Colors Mapping
export const STATUS_COLORS = {
  // Success states
  [BOOKING_STATUS.CONFIRMED]: 'success',
  [BOOKING_STATUS.COMPLETED]: 'success',
  [PAYMENT_STATUS.SUCCEEDED]: 'success',
  [PACKAGE_STATUS.ACTIVE]: 'success',
  
  // Warning states
  'pending': 'warning', // Used by both BOOKING_STATUS and PAYMENT_STATUS
  [PAYMENT_STATUS.PROCESSING]: 'warning',
  
  // Error states
  [BOOKING_STATUS.CANCELLED]: 'error',
  [BOOKING_STATUS.CANCELED]: 'error',
  [PAYMENT_STATUS.FAILED]: 'error',
  
  // Info states
  [PAYMENT_STATUS.REFUNDED]: 'info',
  [BOOKING_STATUS.NO_SHOW]: 'info',
  [BOOKING_STATUS.NOSHOW]: 'info',
  [PACKAGE_STATUS.INACTIVE]: 'info',
  [PACKAGE_STATUS.EXPIRED]: 'info',
} as const;

// Authentication Token Keys
export const AUTH_TOKEN = {
  PORTAL: 'auth_token',
  ADMIN: 'token',
} as const;

// Admin Resource Names
export const ADMIN_RESOURCES = [
  'bookings',
  'promotions',
  'user_subscriptions',
  'packages',
  'subscriptions',
  'gift-cards',
  'gift-card-transactions',
  'payments',
  'payment_items',
  'session-transactions',
  'user_packages',
  'admin_users',
  'users',
  'stripe-transactions',
  'audit-logs',
  'resources',
  'resource-working-hours',
  'resource-closing-periods',
  'services',
  'content-pages',
  'global-closing-periods',
  'global-opening-periods',
  'coupon-codes',
  'coupon-code-usages',
  'galleries',
  'gallery-images',
] as const;

export type AdminResource = typeof ADMIN_RESOURCES[number];

// API Prefixes
export const API_PREFIX = {
  ADMIN: '/api/admin',
  PUBLIC: '',
} as const;

// Common Error Messages
export const ERROR_MESSAGES = {
  NETWORK_ERROR: 'Network error occurred',
  UNAUTHORIZED: 'Unauthorized access',
  NOT_FOUND: 'Resource not found',
  SERVER_ERROR: 'Server error occurred',
  VALIDATION_ERROR: 'Validation failed',
} as const;

// Currency Constants
export const CURRENCY = {
  CENTS_PER_UNIT: 100,
  DEFAULT_CURRENCY: 'EUR',
  SYMBOL: '€',
} as const;

// Date Format Constants
export const DATE_FORMAT = {
  DEFAULT: 'YYYY-MM-DD',
  DISPLAY: 'DD/MM/YYYY',
  TIME: 'HH:mm',
  DATETIME: 'YYYY-MM-DD HH:mm:ss',
} as const;

// Timezone Constants
export const TALLINN_TIMEZONE = 'Europe/Tallinn';